import {
    AlbEntity,
    BillingEntity,
    CloudDesktopEntity,
    ComputeEntity,
    DataTransferEntity,
    DeployEntity,
    DnsEntity,
    GenericEntity,
    GitlabEntity,
    IamEntity,
    IotCoreEntity,
    K8sEntity,
    LoadTestingEntity,
    LogbrokerEntity,
    LoggingEntity,
    MdbEntity,
    MessageQueueEntity,
    SmartcaptchaEntity,
    StatusIllustrationScope,
    StatusIllustrationStatus,
    StatusIllustrationVersion,
    SupportEntity,
    YdbEntity,
    YqlEntity,
} from './types';

type IllustrationVersionStatus = Partial<Record<StatusIllustrationVersion, true | undefined>>;
type IllustrationStatus = Partial<Record<StatusIllustrationStatus, IllustrationVersionStatus>>;

export const availableIllustrations: Record<
    StatusIllustrationScope,
    | Record<AlbEntity, IllustrationStatus>
    | Record<BillingEntity, IllustrationStatus>
    | Record<CloudDesktopEntity, IllustrationStatus>
    | Record<ComputeEntity, IllustrationStatus>
    | Record<DataTransferEntity, IllustrationStatus>
    | Record<DeployEntity, IllustrationStatus>
    | Record<DnsEntity, IllustrationStatus>
    | Record<GenericEntity, IllustrationStatus>
    | Record<GitlabEntity, IllustrationStatus>
    | Record<IamEntity, IllustrationStatus>
    | Record<IotCoreEntity, IllustrationStatus>
    | Record<K8sEntity, IllustrationStatus>
    | Record<LoadTestingEntity, IllustrationStatus>
    | Record<LogbrokerEntity, IllustrationStatus>
    | Record<LoggingEntity, IllustrationStatus>
    | Record<MdbEntity, IllustrationStatus>
    | Record<MessageQueueEntity, IllustrationStatus>
    | Record<SmartcaptchaEntity, IllustrationStatus>
    | Record<SupportEntity, IllustrationStatus>
    | Record<YdbEntity, IllustrationStatus>
    | Record<YqlEntity, IllustrationStatus>
> = {
    [StatusIllustrationScope.Alb]: {
        [AlbEntity.BackendGroups]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.Backends]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.HealthChecks]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.HttpRouters]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.Listeners]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.LoadBalancers]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [AlbEntity.TargetGroups]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.Targets]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.VirtualHosts]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [AlbEntity.Generic]: {},
    },
    [StatusIllustrationScope.Billing]: {
        [BillingEntity.Account]: {
            [StatusIllustrationStatus.Empty]: {v1: true, v2: true},
        },
        [BillingEntity.Detail]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [BillingEntity.Graph]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [BillingEntity.Resource]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [BillingEntity.Generic]: {},
    },
    [StatusIllustrationScope.CloudDesktop]: {
        [CloudDesktopEntity.Desktops]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [CloudDesktopEntity.LogRecords]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [CloudDesktopEntity.Generic]: {},
    },
    [StatusIllustrationScope.Compute]: {
        [ComputeEntity.Coi]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Console]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Disk]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.DiskPlacementGroup]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Filesystem]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Host]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.HostGroup]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Image]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Instance]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.InstanceGroup]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.InstancePlacementGroup]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Log]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Network]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Schedule]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Serial]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Snapshot]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [ComputeEntity.Generic]: {},
    },
    [StatusIllustrationScope.DataTransfer]: {
        [DataTransferEntity.BadRequest]: {
            [StatusIllustrationStatus.BadRequest]: {v1: true},
        },
        [DataTransferEntity.Endpoints]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [DataTransferEntity.Logs]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [DataTransferEntity.Transfers]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [DataTransferEntity.Generic]: {},
    },
    [StatusIllustrationScope.Deploy]: {
        [DeployEntity.Account]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [DeployEntity.Project]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [DeployEntity.Generic]: {},
    },
    [StatusIllustrationScope.Dns]: {
        [DnsEntity.Records]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [DnsEntity.Zones]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [DnsEntity.Generic]: {},
    },
    [StatusIllustrationScope.Generic]: {
        [GenericEntity.Chart]: {
            [StatusIllustrationStatus.AccessDenied]: {v2: true},
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [GenericEntity.Directory]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [GenericEntity.Generic]: {
            [StatusIllustrationStatus.AccessDenied]: {v1: true, v2: true},
            [StatusIllustrationStatus.BadRequest]: {v1: true, v2: true},
            [StatusIllustrationStatus.Created]: {v1: true, v2: true},
            [StatusIllustrationStatus.Empty]: {v1: true, v2: true},
            [StatusIllustrationStatus.NotFound]: {v1: true, v2: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true, v2: true},
            [StatusIllustrationStatus.InternalError]: {v1: true, v2: true},
        },
        [GenericEntity.Operation]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [GenericEntity.Query]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
    },
    [StatusIllustrationScope.Gitlab]: {
        [GitlabEntity.Instances]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [GitlabEntity.OmniAuth]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [GitlabEntity.Generic]: {},
    },
    [StatusIllustrationScope.Iam]: {
        [IamEntity.Subject]: {
            [StatusIllustrationStatus.NoSearchResults]: {v2: true},
        },
        [IamEntity.Generic]: {},
    },
    [StatusIllustrationScope.IotCore]: {
        [IotCoreEntity.Broker]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [IotCoreEntity.Certificate]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [IotCoreEntity.Device]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [IotCoreEntity.Password]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [IotCoreEntity.Registry]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [IotCoreEntity.Generic]: {},
    },
    [StatusIllustrationScope.K8s]: {
        [K8sEntity.Clusters]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [K8sEntity.NodeGroups]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [K8sEntity.Generic]: {},
    },
    [StatusIllustrationScope.LoadTesting]: {
        [LoadTestingEntity.Agents]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [LoadTestingEntity.Buckets]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [LoadTestingEntity.Tests]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [LoadTestingEntity.Generic]: {},
    },
    [StatusIllustrationScope.Logbroker]: {
        [LogbrokerEntity.Account]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.Consumer]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.Directory]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.Generic]: {
            [StatusIllustrationStatus.AccessDenied]: {v2: true},
        },
        [LogbrokerEntity.Partition]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.Preview]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.ReadMetric]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
        },
        [LogbrokerEntity.Topic]: {
            [StatusIllustrationStatus.Empty]: {v2: true},
            [StatusIllustrationStatus.InternalError]: {v2: true},
        },
    },
    [StatusIllustrationScope.Logging]: {
        [LoggingEntity.Logs]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [LoggingEntity.Generic]: {},
    },
    [StatusIllustrationScope.Mdb]: {
        [MdbEntity.Auth]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Backups]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Clusters]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Connectors]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Databases]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Datalens]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.DatalensAccess]: {
            [StatusIllustrationStatus.AccessDenied]: {v1: true},
        },
        [MdbEntity.Dictionaries]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Dictionary]: {
            [StatusIllustrationStatus.NotFound]: {v1: true},
        },
        [MdbEntity.Explore]: {
            [StatusIllustrationStatus.BadRequest]: {v1: true},
        },
        [MdbEntity.FormatSchemas]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Hosts]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Jobs]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Logs]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.MlModels]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Network]: {
            [StatusIllustrationStatus.BadRequest]: {v1: true},
        },
        [MdbEntity.PerformanceDiagnostics]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.PerformanceDiagnosticsSessions]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.PerformanceDiagnosticsStatements]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Profilling]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Shards]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Topics]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Users]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MdbEntity.Generic]: {},
    },
    [StatusIllustrationScope.MessageQueue]: {
        [MessageQueueEntity.Queue]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [MessageQueueEntity.Generic]: {},
    },
    [StatusIllustrationScope.Smartcaptcha]: {
        [SmartcaptchaEntity.CaptchaSettingsList]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
            [StatusIllustrationStatus.NoSearchResults]: {v1: true},
        },
        [SmartcaptchaEntity.Generic]: {},
    },
    [StatusIllustrationScope.Support]: {
        [SupportEntity.Reply]: {
            [StatusIllustrationStatus.Created]: {v2: true},
        },
        [SupportEntity.Tickets]: {
            [StatusIllustrationStatus.NotFound]: {v1: true},
        },
        [SupportEntity.Generic]: {},
    },
    [StatusIllustrationScope.Ydb]: {
        [YdbEntity.Backup]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [YdbEntity.Database]: {
            [StatusIllustrationStatus.Empty]: {v1: true},
        },
        [YdbEntity.Generic]: {},
    },
    [StatusIllustrationScope.Yql]: {
        [YqlEntity.Generic]: {
            [StatusIllustrationStatus.InternalError]: {v2: true},
        },
    },
};
